import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Autocomplete,
  Collapse,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { Plus, Trash2, Edit2, Building2, Search, Mail, KeyRound, Filter, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';

// Map des labels de rôles
const roleLabels = {
  CLIENT: 'Client',
  CLIENTADMIN: 'Admin Client',
  DEV: 'Développeur',
  TECHNICIEN: 'Technicien',
  ADMIN: 'Administrateur',
  SUPERADMIN: 'SuperAdmin'
};

const roleColors = {
  SUPERADMIN: 'primary',
  ADMIN: 'secondary',
  DEV: 'info',
  TECHNICIEN: 'info',
  CLIENTADMIN: 'warning',
  CLIENT: 'default'
};

function ClientManager() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const companyFilter = searchParams.get('company');
  
  // États pour les filtres
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    showInternal: false, // Masquer Global TI par défaut
    showActive: true,
    showPending: true,
    showInactive: false,
    roles: {
      CLIENT: true,
      CLIENTADMIN: true,
      DEV: true,
      TECHNICIEN: true,
      ADMIN: true,
      SUPERADMIN: true
    }
  });
  
  // État pour les groupes collapsés (par entreprise)
  const [expandedCompanies, setExpandedCompanies] = useState({});
  
  const [newInvite, setNewInvite] = useState({ 
    email: '', 
    firstName: '', 
    lastName: '', 
    companyId: companyFilter || '',
    role: 'CLIENT'
  });
  
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // ID de l'utilisateur en cours d'action
  const [actionSuccess, setActionSuccess] = useState('');

  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const isAdmin = currentUser?.role === 'ADMIN' || isSuperAdmin;
  const isClientAdmin = currentUser?.role === 'CLIENTADMIN';

  // Déterminer le titre de la page selon le rôle
  const pageTitle = isClientAdmin 
    ? 'Utilisateurs de mon entreprise' 
    : 'Gestion des Utilisateurs';

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      let url = '/api/auth/list-users';
      
      // Si filtre par entreprise (pour admin)
      if (companyFilter && isAdmin) {
        url += `?companyId=${companyFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  const fetchCompanies = async () => {
    // Seulement pour admin/superadmin
    if (!isAdmin) return;
    
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/companies/clients', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) setCompanies(data);
    } catch (err) {
      console.error('Failed to fetch companies');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, [companyFilter]);

  const handleInvite = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      
      // Utiliser la route appropriée selon le rôle
      const url = isClientAdmin ? '/api/auth/invite-to-company' : '/api/auth/invite';
      
      const body = isClientAdmin 
        ? { email: newInvite.email, firstName: newInvite.firstName, lastName: newInvite.lastName }
        : newInvite;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (response.ok) {
        setOpen(false);
        setNewInvite({ email: '', firstName: '', lastName: '', companyId: companyFilter || '', role: 'CLIENT' });
        fetchUsers();
      } else {
        alert(data.error || 'Erreur lors de l\'invitation');
      }
    } catch (err) {
      alert('Erreur réseau');
    }
    setLoading(false);
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/auth/user/${editingUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: editingUser.firstName,
          lastName: editingUser.lastName,
          companyId: editingUser.companyId,
          role: editingUser.role,
          status: editingUser.status
        })
      });
      if (response.ok) {
        setEditOpen(false);
        fetchUsers();
      } else {
        const data = await response.json();
        alert(data.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      alert('Erreur réseau');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) return;
    
    setDeletingUserId(id);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/auth/user/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchUsers();
      } else {
        const data = await response.json();
        alert(data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      alert('Erreur réseau');
    }
    setDeletingUserId(null);
  };

  const openEdit = (u) => {
    setEditingUser({ ...u });
    setEditOpen(true);
  };

  // Renvoyer l'invitation
  const handleResendInvitation = async (userId) => {
    setActionLoading(userId);
    setActionSuccess('');
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/auth/user/${userId}/resend-invitation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setActionSuccess(`Invitation renvoyée avec succès.`);
        setTimeout(() => setActionSuccess(''), 3000);
      } else {
        alert(data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      alert('Erreur réseau');
    }
    setActionLoading(null);
  };

  // Envoyer reset mot de passe
  const handleSendPasswordReset = async (userId) => {
    setActionLoading(userId);
    setActionSuccess('');
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/auth/user/${userId}/send-password-reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setActionSuccess(`Email de réinitialisation envoyé.`);
        setTimeout(() => setActionSuccess(''), 3000);
      } else {
        alert(data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      alert('Erreur réseau');
    }
    setActionLoading(null);
  };

  // Déterminer si l'utilisateur peut être supprimé
  const canDelete = (u) => {
    // Ne peut pas se supprimer soi-même
    if (u.id === currentUser.id) return false;
    
    // CLIENTADMIN ne peut supprimer que les CLIENT de son entreprise
    if (isClientAdmin) {
      return u.role === 'CLIENT' && u.companyId === currentUser.companyId;
    }
    
    // SUPERADMIN peut tout supprimer sauf le dernier superadmin
    if (isSuperAdmin) return true;
    
    // ADMIN ne peut pas supprimer SUPERADMIN ou ADMIN
    if (isAdmin) {
      return !['SUPERADMIN', 'ADMIN'].includes(u.role);
    }
    
    return false;
  };

  // Déterminer si l'utilisateur peut être édité
  const canEdit = (u) => {
    if (isClientAdmin) return false; // CLIENTADMIN ne peut pas éditer
    if (isSuperAdmin) return true;
    if (isAdmin) return !['SUPERADMIN'].includes(u.role);
    return false;
  };

  // Obtenir les rôles disponibles pour l'invitation selon le contexte
  const getAvailableRolesForInvite = () => {
    if (isClientAdmin) {
      return []; // Pas de choix, toujours CLIENT
    }
    
    const roles = [
      { value: 'CLIENT', label: 'Client' },
      { value: 'DEV', label: 'Développeur' },
      { value: 'TECHNICIEN', label: 'Technicien' },
      { value: 'ADMIN', label: 'Administrateur' }
    ];
    
    if (isSuperAdmin) {
      roles.push({ value: 'SUPERADMIN', label: 'SuperAdmin' });
    }
    
    return roles;
  };

  // Vérifier si on doit montrer le sélecteur d'entreprise
  const showCompanySelector = isAdmin && ['CLIENT', 'CLIENTADMIN'].includes(newInvite.role);

  // Filtrer et grouper les utilisateurs
  const filteredAndGroupedUsers = useMemo(() => {
    // D'abord filtrer
    const filtered = users.filter(u => {
      // Filtre recherche
      const matchesSearch = searchTerm === '' || 
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.companyName || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtre entreprise interne
      const matchesInternal = filters.showInternal || !u.isInternalCompany;
      
      // Filtre statut
      const matchesStatus = 
        (filters.showActive && u.status === 'ACTIVE') ||
        (filters.showPending && u.status === 'PENDING') ||
        (filters.showInactive && u.status === 'INACTIVE');
      
      // Filtre rôle
      const matchesRole = filters.roles[u.role];
      
      return matchesSearch && matchesInternal && matchesStatus && matchesRole;
    });
    
    // Puis grouper par entreprise
    const grouped = filtered.reduce((acc, user) => {
      const companyId = user.companyId || 'no-company';
      if (!acc[companyId]) {
        acc[companyId] = {
          companyId,
          companyName: user.companyName || 'Sans entreprise',
          isInternal: user.isInternalCompany,
          users: []
        };
      }
      acc[companyId].users.push(user);
      return acc;
    }, {});
    
    // Convertir en tableau et trier (internes en dernier)
    return Object.values(grouped).sort((a, b) => {
      if (a.isInternal && !b.isInternal) return 1;
      if (!a.isInternal && b.isInternal) return -1;
      return a.companyName.localeCompare(b.companyName);
    });
  }, [users, searchTerm, filters]);

  // Toggle expansion d'une entreprise
  const toggleCompanyExpand = (companyId) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  // Expand/Collapse all
  const expandAll = () => {
    const allExpanded = {};
    filteredAndGroupedUsers.forEach(g => { allExpanded[g.companyId] = true; });
    setExpandedCompanies(allExpanded);
  };
  
  const collapseAll = () => {
    setExpandedCompanies({});
  };

  // Compter les filtres actifs
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (!filters.showInternal) count++; // Masquer interne = filtre actif
    if (!filters.showActive) count++;
    if (!filters.showPending) count++;
    if (!filters.showInactive) count++;
    Object.values(filters.roles).forEach(v => { if (!v) count++; });
    return count;
  }, [filters]);

  // Total utilisateurs filtrés
  const totalFilteredUsers = filteredAndGroupedUsers.reduce((sum, g) => sum + g.users.length, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{pageTitle}</Typography>
          {companyFilter && isAdmin && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Filtré par entreprise
            </Typography>
          )}
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Plus size={18} />}
          onClick={() => setOpen(true)}
        >
          {isClientAdmin ? 'Inviter un collègue' : 'Nouvelle Invitation'}
        </Button>
      </Box>

      {isClientAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          En tant qu'administrateur client, vous pouvez inviter des utilisateurs dans votre entreprise ({currentUser.companyName}).
        </Alert>
      )}

      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setActionSuccess('')}>
          {actionSuccess}
        </Alert>
      )}

      {/* Barre de recherche et filtres */}
      {isAdmin && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper', backgroundImage: 'none' }}>
          <Stack spacing={2}>
            {/* Recherche */}
            <TextField
              fullWidth
              placeholder="Rechercher par nom, email ou entreprise..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Boutons filtres et expand/collapse */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<Filter size={16} />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ textTransform: 'none' }}
                >
                  Filtres {activeFilterCount > 0 && `(${activeFilterCount})`}
                </Button>
                <Button size="small" onClick={expandAll} sx={{ textTransform: 'none' }}>
                  Tout déplier
                </Button>
                <Button size="small" onClick={collapseAll} sx={{ textTransform: 'none' }}>
                  Tout replier
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {totalFilteredUsers} utilisateur(s) • {filteredAndGroupedUsers.length} entreprise(s)
              </Typography>
            </Box>

            {/* Section filtres dépliable */}
            <Collapse in={showFilters}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(255,255,255,0.02)', 
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 2
                }}
              >
                <Stack spacing={2}>
                  {/* Filtres entreprise */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Entreprise
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={filters.showInternal}
                          onChange={(e) => setFilters(prev => ({ ...prev, showInternal: e.target.checked }))}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Building2 size={14} color="#6366f1" />
                          <Typography variant="body2">Afficher Global TI (interne)</Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Filtres statut */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Statut
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={filters.showActive}
                            onChange={(e) => setFilters(prev => ({ ...prev, showActive: e.target.checked }))}
                            size="small"
                          />
                        }
                        label={<Chip label="Actif" size="small" color="success" sx={{ height: 20 }} />}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={filters.showPending}
                            onChange={(e) => setFilters(prev => ({ ...prev, showPending: e.target.checked }))}
                            size="small"
                          />
                        }
                        label={<Chip label="En attente" size="small" color="warning" sx={{ height: 20 }} />}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={filters.showInactive}
                            onChange={(e) => setFilters(prev => ({ ...prev, showInactive: e.target.checked }))}
                            size="small"
                          />
                        }
                        label={<Chip label="Inactif" size="small" color="default" sx={{ height: 20 }} />}
                      />
                    </Stack>
                  </Box>

                  {/* Filtres rôles */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Rôles
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      {Object.entries(roleLabels).map(([role, label]) => (
                        <FormControlLabel
                          key={role}
                          control={
                            <Checkbox 
                              checked={filters.roles[role]}
                              onChange={(e) => setFilters(prev => ({ 
                                ...prev, 
                                roles: { ...prev.roles, [role]: e.target.checked }
                              }))}
                              size="small"
                            />
                          }
                          label={
                            <Chip 
                              label={label} 
                              size="small" 
                              color={roleColors[role] || 'default'} 
                              variant="outlined"
                              sx={{ height: 20 }} 
                            />
                          }
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Bouton reset */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={() => setFilters({
                        showInternal: false,
                        showActive: true,
                        showPending: true,
                        showInactive: false,
                        roles: {
                          CLIENT: true,
                          CLIENTADMIN: true,
                          DEV: true,
                          TECHNICIEN: true,
                          ADMIN: true,
                          SUPERADMIN: true
                        }
                      })}
                      sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                      Réinitialiser les filtres
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Collapse>
          </Stack>
        </Paper>
      )}

      {/* Liste groupée par entreprise */}
      <Stack spacing={2}>
        {filteredAndGroupedUsers.map((group) => (
          <Paper 
            key={group.companyId} 
            sx={{ 
              bgcolor: 'background.paper', 
              backgroundImage: 'none',
              border: group.isInternal ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid rgba(255,255,255,0.06)',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Header entreprise (cliquable) */}
            <Box
              onClick={() => toggleCompanyExpand(group.companyId)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                cursor: 'pointer',
                bgcolor: group.isInternal ? 'rgba(99, 102, 241, 0.05)' : 'rgba(0, 212, 170, 0.03)',
                '&:hover': { bgcolor: group.isInternal ? 'rgba(99, 102, 241, 0.1)' : 'rgba(0, 212, 170, 0.06)' }
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton size="small" sx={{ p: 0 }}>
                  {expandedCompanies[group.companyId] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </IconButton>
                <Building2 size={20} color={group.isInternal ? '#6366f1' : '#00d4aa'} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {group.companyName}
                </Typography>
                {group.isInternal && (
                  <Chip label="Interne" size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
                )}
              </Stack>
              <Chip 
                icon={<Users size={14} />}
                label={`${group.users.length} utilisateur(s)`} 
                size="small" 
                variant="outlined"
                sx={{ height: 24 }}
              />
            </Box>

            {/* Table des utilisateurs (collapsible) */}
            <Collapse in={expandedCompanies[group.companyId]}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '40%' }}>Utilisateur</TableCell>
                      <TableCell align="center" sx={{ width: '20%' }}>Rôle</TableCell>
                      <TableCell align="center" sx={{ width: '20%' }}>Statut</TableCell>
                      <TableCell align="right" sx={{ width: '20%' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.users.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell sx={{ width: '40%' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.firstName} {u.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ width: '20%' }}>
                          <Chip 
                            label={roleLabels[u.role] || u.role} 
                            size="small" 
                            color={roleColors[u.role] || 'default'}
                            variant="outlined"
                            sx={{ minWidth: '110px' }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ width: '20%' }}>
                          <Chip 
                            label={u.status === 'ACTIVE' ? 'Actif' : u.status === 'PENDING' ? 'En attente' : 'Inactif'} 
                            size="small"
                            color={u.status === 'ACTIVE' ? 'success' : u.status === 'PENDING' ? 'warning' : 'default'}
                            sx={{ minWidth: '90px' }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: '20%' }}>
                          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                            {/* Renvoyer invitation (PENDING) */}
                            {u.status === 'PENDING' && (
                              <Tooltip title="Renvoyer l'invitation">
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => { e.stopPropagation(); handleResendInvitation(u.id); }}
                                  disabled={actionLoading === u.id}
                                  sx={{ color: 'warning.main' }}
                                >
                                  <Mail size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Reset password (ACTIVE) */}
                            {u.status === 'ACTIVE' && u.id !== currentUser.id && (
                              <Tooltip title="Envoyer reset mot de passe">
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => { e.stopPropagation(); handleSendPasswordReset(u.id); }}
                                  disabled={actionLoading === u.id}
                                  sx={{ color: 'info.main' }}
                                >
                                  <KeyRound size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEdit(u) && (
                              <Tooltip title="Modifier">
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => { e.stopPropagation(); openEdit(u); }} 
                                  color="primary"
                                >
                                  <Edit2 size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Supprimer">
                              <span>
                                <IconButton 
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(u.id); }} 
                                  disabled={!canDelete(u) || deletingUserId === u.id}
                                  color="error"
                                >
                                  <Trash2 size={16} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        ))}

        {filteredAndGroupedUsers.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper', backgroundImage: 'none' }}>
            <Typography color="text.secondary">Aucun utilisateur trouvé avec ces filtres</Typography>
          </Paper>
        )}
      </Stack>

      {/* Invitation Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {isClientAdmin ? 'Inviter un collègue' : 'Inviter un utilisateur'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Sélecteur de rôle (seulement pour admin) */}
            {isAdmin && (
              <FormControl fullWidth size="small">
                <InputLabel>Type d'usager</InputLabel>
                <Select
                  value={newInvite.role}
                  label="Type d'usager"
                  onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value })}
                >
                  {getAvailableRolesForInvite().map(r => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField 
              label="Email" 
              fullWidth 
              size="small"
              value={newInvite.email}
              onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Prénom" 
                fullWidth 
                size="small"
                value={newInvite.firstName}
                onChange={(e) => setNewInvite({ ...newInvite, firstName: e.target.value })}
              />
              <TextField 
                label="Nom" 
                fullWidth 
                size="small"
                value={newInvite.lastName}
                onChange={(e) => setNewInvite({ ...newInvite, lastName: e.target.value })}
              />
            </Box>

            {/* Sélecteur d'entreprise avec recherche (seulement pour admin et rôles client) */}
            {showCompanySelector && (
              <Autocomplete
                size="small"
                options={companies}
                getOptionLabel={(option) => option.name || ''}
                value={companies.find(c => c.id === newInvite.companyId) || null}
                onChange={(_, newValue) => setNewInvite({ ...newInvite, companyId: newValue?.id || '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Entreprise"
                    placeholder="Rechercher une entreprise..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search size={16} style={{ marginLeft: 8, marginRight: 4, opacity: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Building2 size={14} color="#00d4aa" />
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="Aucune entreprise trouvée"
              />
            )}

            {isClientAdmin && (
              <Alert severity="info" size="small">
                L'utilisateur sera automatiquement ajouté à votre entreprise : {currentUser.companyName}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleInvite} 
            disabled={!newInvite.email || loading || (showCompanySelector && !newInvite.companyId)}
          >
            {loading ? 'Envoi...' : 'Envoyer l\'Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Modifier l'utilisateur</DialogTitle>
        <DialogContent>
          {editingUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Rôle</InputLabel>
                <Select
                  value={editingUser.role}
                  label="Rôle"
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                >
                  <MenuItem value="CLIENT">Client</MenuItem>
                  <MenuItem value="CLIENTADMIN">Admin Client</MenuItem>
                  <MenuItem value="DEV">Développeur</MenuItem>
                  <MenuItem value="TECHNICIEN">Technicien</MenuItem>
                  <MenuItem value="ADMIN">Administrateur</MenuItem>
                  {isSuperAdmin && <MenuItem value="SUPERADMIN">SuperAdmin</MenuItem>}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={editingUser.status}
                  label="Statut"
                  onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                >
                  <MenuItem value="ACTIVE">Actif</MenuItem>
                  <MenuItem value="PENDING">En attente</MenuItem>
                  <MenuItem value="INACTIVE">Inactif</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  label="Prénom" 
                  fullWidth 
                  size="small"
                  value={editingUser.firstName || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                />
                <TextField 
                  label="Nom" 
                  fullWidth 
                  size="small"
                  value={editingUser.lastName || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                />
              </Box>

              {/* Sélecteur d'entreprise avec recherche pour les rôles client */}
              {['CLIENT', 'CLIENTADMIN'].includes(editingUser.role) && (
                <Autocomplete
                  size="small"
                  options={companies}
                  getOptionLabel={(option) => option.name || ''}
                  value={companies.find(c => c.id === editingUser.companyId) || null}
                  onChange={(_, newValue) => setEditingUser({ ...editingUser, companyId: newValue?.id || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Entreprise"
                      placeholder="Rechercher une entreprise..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <Search size={16} style={{ marginLeft: 8, marginRight: 4, opacity: 0.5 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Building2 size={14} color="#00d4aa" />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText="Aucune entreprise trouvée"
                />
              )}

              {['DEV', 'TECHNICIEN', 'ADMIN', 'SUPERADMIN'].includes(editingUser.role) && (
                <Alert severity="info">
                  Les rôles internes (DEV, TECHNICIEN, ADMIN, SUPERADMIN) sont automatiquement associés à Global TI.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleUpdate}
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientManager;
